import { type ReactElement, useEffect, useState } from 'react'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { fill, label, track } from './ScoreCircle.css'

const ANIMATION_DURATION = 1500

type ScoreCircleProps = {
  score: number
  size?: number
  tooltip?: string
  className?: string
}

export const ScoreCircle = ({
  score,
  size = 44,
  tooltip,
  className
}: ScoreCircleProps): ReactElement => {
  const [displayed, setDisplayed] = useState(0)

  useEffect(() => {
    const start = performance.now()
    let raf: number

    const animate = (now: number) => {
      const progress = Math.min((now - start) / ANIMATION_DURATION, 1)
      const eased = 1 - (1 - progress) ** 3
      setDisplayed(Math.round(eased * score))
      if (progress < 1) raf = requestAnimationFrame(animate)
    }

    raf = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(raf)
  }, [score])

  const center = size / 2
  const strokeWidth = size / 9
  const radius = center - strokeWidth
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - displayed / 100)
  const textY = center + strokeWidth * 0.8

  const svg = (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={tooltip ?? `Score : ${score}%`}
      className={className}
    >
      <circle
        className={track}
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
      />
      <circle
        className={fill}
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
      />
      <text className={label} x={center} y={textY} textAnchor="middle">
        {displayed}%
      </text>
    </svg>
  )

  if (tooltip) {
    return (
      <Tooltip asChild text={tooltip} direction="top">
        {svg}
      </Tooltip>
    )
  }

  return svg
}
